import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useStaff } from "./StaffLayout";

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function StaffReport() {
  const { t } = useStaff();
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [projectId, setProjectId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [content, setContent] = useState("");
  const [hours, setHours] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = () => {
    api.get("/staff/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/staff/daily-reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  };
  useEffect(load, []);

  const sites = projects.find((p) => p.id === projectId)?.sites || [];

  const submit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    setMessage("");
    try {
      await api.post("/staff/daily-reports", {
        date, project_id: projectId, site_id: siteId,
        content, hours: Number(hours) || 0, notes,
      });
      setMessage(t("report_saved"));
      setContent(""); setHours(""); setNotes("");
      load();
    } catch {
      setMessage(t("upload_failed"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-line bg-ink px-3 py-3 text-sm text-white outline-none focus:border-accent";

  return (
    <div data-testid="staff-report-page" className="mx-auto max-w-lg space-y-8 px-4 py-6">
      <section className="border border-line bg-coal p-4">
        <h2 data-testid="staff-report-heading" className="font-mincho text-lg font-bold text-white">{t("report_new")}</h2>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-gray-500">{t("date_label")}</label>
              <input data-testid="staff-report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-gray-500">{t("hours_label")}</label>
              <input data-testid="staff-report-hours" type="number" step="0.5" min="0" value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} placeholder="8" />
            </div>
          </div>
          <select data-testid="staff-report-project" value={projectId} onChange={(e) => { setProjectId(e.target.value); setSiteId(""); }} className={inputCls}>
            <option value="">{t("select_project")}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>[{p.code || "-"}] {p.name}</option>)}
          </select>
          <select data-testid="staff-report-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputCls}>
            <option value="">{t("select_site")}</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <textarea data-testid="staff-report-content" value={content} onChange={(e) => setContent(e.target.value)} placeholder={t("work_content")} rows={4} className={inputCls} />
          <textarea data-testid="staff-report-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("report_note")} rows={2} className={inputCls} />
          {message && <p data-testid="staff-report-message" className={`text-sm ${message === t("report_saved") ? "text-emerald-400" : "text-red-400"}`}>{message}</p>}
          <button data-testid="staff-report-submit" onClick={submit} disabled={saving || !content.trim()} className="w-full bg-accent py-4 text-sm font-bold text-ink transition-colors hover:bg-accent-hover disabled:opacity-40">
            {saving ? "..." : t("send_report")}
          </button>
        </div>
      </section>

      <section>
        <h2 data-testid="staff-report-history-heading" className="font-mincho text-lg font-bold text-white">{t("my_reports")}</h2>
        <div className="mt-3 space-y-2">
          {reports.length === 0 && <p data-testid="staff-report-empty" className="text-sm text-gray-500">{t("no_reports")}</p>}
          {reports.map((r) => (
            <div key={r.id} data-testid={`staff-report-item-${r.id}`} className="border border-line bg-coal p-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-accent">{r.date}</span>
                {r.hours > 0 && <span className="text-[10px] text-gray-500">{r.hours}h</span>}
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-300">{r.content}</p>
              {r.notes && <p className="mt-1 text-xs text-gray-500">{r.notes}</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
